import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { RefreshCw, CheckCircle, AlertCircle, Users, FileText } from 'lucide-react';
import { generateMedicalRecordNumber } from '../../utils/medicalRecordGenerator';
import { supabase } from '../../config/supabase';
import { SupabasePatientRow } from '../../types/supabase';
import { useAuth } from '../../context/AuthContext';

interface PatientToMigrate extends SupabasePatientRow {
  needsMigration: boolean;
  newMedicalRecordNumber?: string;
}

interface MigrationStats {
  total: number;
  withSexo: number;
  withoutSexo: number;
  migrated: number;
}

const MigrateMedicalRecordsPage: React.FC = () => {
  const [patients, setPatients] = useState<PatientToMigrate[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, selectedClient } = useAuth();
  const [migrating, setMigrating] = useState(false);
  const [stats, setStats] = useState<MigrationStats>({
    total: 0,
    withSexo: 0,
    withoutSexo: 0,
    migrated: 0
  });

  const [selectedGender, setSelectedGender] = useState<Record<string, 'Masculino' | 'Feminino'>>({});
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (user && selectedClient) {
      loadPatients(selectedClient.clientId);
    }
  }, [user, selectedClient]);

  const loadPatients = async (clientId: string) => {
    try {
      setLoading(true);

      const { data: patientsDataSupabase, error } = await supabase
        .from("patients")
        .select("*", { count: "exact" })
        .eq("clientId", clientId);

      if (error) {
        throw error;
      }

      const patientsToMigrate: PatientToMigrate[] = patientsDataSupabase.map(p => {
        const patientGenderFromDb = p.patientGender as PatientToMigrate["patientGender"];
        const needsMigration = !patientGenderFromDb || p.medical_record_number === null; // Adicionado verificação para medical_record_number

        return {
          ...p,
          patientId: p.patientId,
          needsMigration: needsMigration,
          // Mapeie os campos antigos do Firebase para os novos do Supabase se ainda estiverem sendo usados na lógica do gerador
          // Para o gerador de prontuário, ele espera nome, sobrenome, dataNascimento e sexo (M/F)
          // Você pode precisar de uma etapa de transformação aqui se patientFullName não puder ser dividido
          nome: p.patientFullName?.split(" ")[0] || "", // Exemplo, pode precisar de ajuste
          sobrenome: p.patientFullName?.split(" ").slice(1).join(" ") || "", // Exemplo, pode precisar de ajuste
          dataNascimento: p.patientDateOfBirth || "",
          sexo: patientGenderToLegacy(patientGenderFromDb), // Converter para 'M' ou 'F'
        };
      });

      let withSexo = 0;
      let withoutSexo = 0;

      patientsToMigrate.forEach(p => {
        if (p.patientGender) { // Usando o campo Supabase
          withSexo++;
        } else {
          withoutSexo++;
        }
      });

      setPatients(patientsToMigrate);
      setStats({
        total: patientsToMigrate.length,
        withSexo,
        withoutSexo,
        migrated: 0 // Será atualizado na execução da migração
      });
      setLoading(false);
    } catch (error) {
      console.error("Erro ao carregar pacientes do Supabase:", error);
      toast.error("Erro ao carregar pacientes para migração");
      setLoading(false);
    }
  };

  // Função auxiliar para converter o gênero do Supabase para o formato 'M' | 'F'
  const patientGenderToLegacy = (gender: PatientToMigrate["patientGender"]): "M" | "F" | undefined => {
    if (gender === "Masculino") return "M";
    if (gender === "Feminino") return "F";
    return undefined;
  };

  const handleGenderChange = (patientId: string, gender: 'Masculino' | 'Feminino') => {
    setSelectedGender(prev => ({
      ...prev,
      [patientId]: gender
    }));
  };

  const generatePreview = () => {
    const updatedPatients = patients.map(patient => {
      if (!patient.needsMigration) {
        return patient;
      }

      const gender = selectedGender[patient.patientId];
      if (!gender) {
        return patient;
      }

      try {
        // Extrair ano de criação do paciente para o gerador de prontuário, se disponível.
        // Caso contrário, usar o ano atual ou uma data de criação padrão.
        const creationYear = patient.patientCreatedAt ? new Date(patient.patientCreatedAt).getFullYear() : new Date().getFullYear();

        const newMedicalRecordNumber = generateMedicalRecordNumber({
          nome: patient.patientFullName?.split(" ")[0] || "",
          sobrenome: patient.patientFullName?.split(" ").slice(1).join(" ") || "",
          dataNascimento: patient.patientDateOfBirth || "",
          sexo: gender === "Masculino" ? "M" : "F", // Converter para o formato esperado pelo gerador
          anoCriacao: creationYear
        });

        return {
          ...patient,
          newMedicalRecordNumber
        };
      } catch (error) {
        console.error("Erro ao gerar preview para paciente:", patient.patientId, error);
        return patient;
      }
    });

    setPatients(updatedPatients);
    setPreviewMode(true);
    toast.info("Preview gerado! Revise os novos códigos antes de confirmar.");
  };

  const executeMigration = async () => {
    if (!selectedClient) {
      toast.error("Cliente não selecionado. Não é possível migrar.");
      return;
    }

    const patientsToMigrate = patients.filter(p => p.needsMigration && selectedGender[p.patientId]);

    if (patientsToMigrate.length === 0) {
      toast.warning("Nenhum paciente selecionado para migração");
      return;
    }

    const confirmMessage = `Você está prestes a migrar ${patientsToMigrate.length} paciente(s). Esta ação irá atualizar permanentemente os códigos de prontuário. Deseja continuar?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setMigrating(true);
      let successCount = 0;
      let errorCount = 0;

      for (const patient of patientsToMigrate) {
        try {
          const gender = selectedGender[patient.patientId]; // 'Masculino' | 'Feminino'
          const legacyGender = gender === "Masculino" ? "M" : "F"; // Converter para 'M' | 'F' para o gerador

          const creationYear = patient.patientCreatedAt ? new Date(patient.patientCreatedAt).getFullYear() : new Date().getFullYear();

          const newMedicalRecordNumber = generateMedicalRecordNumber({
            nome: patient.patientFullName?.split(" ")[0] || "",
            sobrenome: patient.patientFullName?.split(" ").slice(1).join(" ") || "",
            dataNascimento: patient.patientDateOfBirth || "",
            sexo: legacyGender,
            anoCriacao: creationYear
          });

          const { error: updateError } = await supabase
            .from("patients")
            .update({
              medical_record_number: newMedicalRecordNumber,
              patientGender: gender, // Salvar o valor completo 'Masculino' ou 'Feminino'
              migrated_at: new Date().toISOString(),
              old_prontuario: patient.medical_record_number // Armazenar o prontuário antigo se existir
            })
            .eq("patientId", patient.patientId);

          if (updateError) {
            throw updateError;
          }

          successCount++;

          setStats(prev => ({
            ...prev,
            migrated: successCount
          }));

        } catch (error) {
          console.error(`Erro ao migrar paciente ${patient.patientId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} paciente(s) migrado(s) com sucesso!`);
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} paciente(s) falharam na migração`);
      }

      await loadPatients(selectedClient.clientId); // Recarrega os pacientes após a migração
      setSelectedGender({});
      setPreviewMode(false);

    } catch (error) {
      console.error('Erro durante migração:', error);
      toast.error('Erro durante o processo de migração');
    } finally {
      setMigrating(false);
    }
  };

  const patientsNeedingMigration = patients.filter(p => p.needsMigration);
  const allGendersSelected = patientsNeedingMigration.every(p => selectedGender[p.patientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <div className="ml-4">
          <p className="text-gray-600">Carregando pacientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Migração de Códigos de Prontuário</h1>
        <p className="text-gray-600">Atualize os códigos de prontuário para o novo formato padronizado</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Pacientes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Users className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Com Sexo Definido</p>
              <p className="text-2xl font-bold text-green-900">{stats.withSexo}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Precisam Migração</p>
              <p className="text-2xl font-bold text-orange-900">{stats.withoutSexo}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Já Migrados</p>
              <p className="text-2xl font-bold text-blue-900">{stats.migrated}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>
      </div>

      {stats.withoutSexo > 0 ? (
        <>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Ação Necessária</h3>
                <p className="mt-2 text-sm text-yellow-700">
                  Existem {stats.withoutSexo} paciente(s) sem o campo sexo definido.
                  Para migrar para o novo formato de código de prontuário (AAMMDD + Sexo + Ano + Iniciais),
                  você precisa definir o sexo de cada paciente abaixo.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Pacientes para Migração</h2>
              <p className="text-sm text-gray-600">Defina o sexo de cada paciente e clique em "Gerar Preview"</p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Prontuário Atual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Nome Completo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Data de Nascimento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Sexo
                    </th>
                    {previewMode && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Novo Prontuário
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patientsNeedingMigration.map((patient) => (
                    <tr key={patient.patientId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {patient.medical_record_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.patientFullName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.patientDateOfBirth}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={selectedGender[patient.patientId] || ''}
                          onChange={(e) => handleGenderChange(patient.patientId, e.target.value as 'Masculino' | 'Feminino')}
                          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          disabled={migrating}
                        >
                          <option value="">Selecione</option>
                          <option value="Masculino">Masculino</option>
                          <option value="Feminino">Feminino</option>
                        </select>
                      </td>
                      {previewMode && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          {patient.newMedicalRecordNumber || '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            {!previewMode ? (
              <button
                onClick={generatePreview}
                disabled={!allGendersSelected || migrating}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Gerar Preview
              </button>
            ) : (
              <>
                <button
                  onClick={() => setPreviewMode(false)}
                  disabled={migrating}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={executeMigration}
                  disabled={migrating}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {migrating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Migrando... ({stats.migrated}/{patientsNeedingMigration.length})
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Confirmar Migração
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-green-900 mb-2">Migração Completa!</h3>
          <p className="text-green-700">
            Todos os pacientes já possuem o campo sexo definido e estão no formato correto.
          </p>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Formato do Novo Código</h3>
        <p className="text-sm text-blue-700 mb-2">
          O novo código de prontuário segue o formato: <strong>AAMMDD + Sexo + Ano de Criação + Iniciais</strong>
        </p>
        <p className="text-sm text-blue-700">
          <strong>Exemplo:</strong> Paciente Carlos Camillo, nascido em 07/11/1983, sexo masculino, cadastrado em 2025 = <strong>831107M2025CC</strong>
        </p>
      </div>
    </div>
  );
};

export default MigrateMedicalRecordsPage;
